import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

/**
 * Drill-down (T-144, RF-37, PRD §11.24; DEC-058): o caminho empresa → perspectiva → objetivo → KPI → unidade vai no
 * endereço de cada tela — projeto, nível, período —, para o breadcrumb e os links levarem o contexto adiante.
 */

export type DrillPath = {
  projectId: string;
  perspectiveId?: string | null;
  objectiveId?: string | null;
  kpiId?: string | null;
  businessUnitId?: string | null;
  year?: number | null;
  month?: number | null;
};

/** Parâmetros do endereço de cada nível (os mesmos nomes em todas as telas). */
export const DRILL_PARAMS = {
  project: 'projeto',
  perspective: 'perspectiva',
  objective: 'objetivo',
  kpi: 'kpi',
  unit: 'unidade',
  year: 'ano',
  month: 'mes',
} as const;

function query(path: DrillPath, keys: (keyof DrillPath)[]) {
  const map: Record<keyof DrillPath, string> = {
    projectId: DRILL_PARAMS.project,
    perspectiveId: DRILL_PARAMS.perspective,
    objectiveId: DRILL_PARAMS.objective,
    kpiId: DRILL_PARAMS.kpi,
    businessUnitId: DRILL_PARAMS.unit,
    year: DRILL_PARAMS.year,
    month: DRILL_PARAMS.month,
  };
  const out = new URLSearchParams();
  for (const key of keys) {
    const value = path[key];
    if (value !== null && value !== undefined && value !== '') out.set(map[key], String(value));
  }
  // O período só vai inteiro (ano e mês juntos).
  if (!path.year || !path.month) {
    out.delete(DRILL_PARAMS.year);
    out.delete(DRILL_PARAMS.month);
  }
  return out.toString();
}

const PERIOD: (keyof DrillPath)[] = ['year', 'month'];

/** Para onde cada nível do drill-down leva. */
export const drillHref = {
  /** Empresa: a Visão Geral (cliente) ou o dashboard executivo (equipe Fluxus, que não tem a Visão Geral). */
  company: (organizationId: string, path: DrillPath, fluxus: boolean) =>
    fluxus
      ? `/empresas/${organizationId}/dashboards/executivo?${query(path, ['projectId', ...PERIOD])}`
      : `/visao-geral?${query(path, ['projectId', ...PERIOD])}`,
  perspective: (organizationId: string, path: DrillPath) =>
    `/empresas/${organizationId}/dashboards/perspectiva?${query(path, ['projectId', 'perspectiveId', ...PERIOD])}`,
  objective: (organizationId: string, path: DrillPath & { objectiveId: string }) =>
    `/empresas/${organizationId}/estrategia/objetivos/${path.objectiveId}?${query(path, ['projectId', ...PERIOD])}`,
  /** O mapa estratégico no mês. */
  map: (organizationId: string, path: DrillPath) =>
    `/empresas/${organizationId}/estrategia/mapa?${query(path, ['projectId', ...PERIOD])}`,
  kpi: (organizationId: string, path: DrillPath) =>
    `/empresas/${organizationId}/indicadores/historico?${query(path, ['projectId', 'kpiId', 'objectiveId', ...PERIOD])}`,
  unit: (organizationId: string, path: DrillPath) =>
    `/empresas/${organizationId}/indicadores/unidades?${query(path, [
      'projectId',
      'kpiId',
      'businessUnitId',
      'objectiveId',
      ...PERIOD,
    ])}`,
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Lê um id do endereço (só um uuid válido; o resto é ignorado). */
export function readId(params: URLSearchParams, key: string): string | null {
  const raw = params.get(key);
  return raw && UUID.test(raw) ? raw : null;
}

/** Período do endereço (?ano=&mes=), quando vem inteiro e válido; senão, null (a tela usa o seu padrão). */
export function readPeriod(params: URLSearchParams): { year: number; month: number } | null {
  const year = Number(params.get(DRILL_PARAMS.year));
  const month = Number(params.get(DRILL_PARAMS.month));
  if (!Number.isInteger(year) || year < 2000 || year > 2100) return null;
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  return { year, month };
}

/** O período pedido no endereço ao abrir a tela (o drill-down chega com ele). */
export function useUrlPeriod() {
  const [params] = useSearchParams();
  return readPeriod(params);
}

/**
 * Período no endereço (?ano=&mes=) como estado da tela (T-144c): vem do link (dentro do horizonte), vai ao endereço
 * quando a pessoa troca e acompanha uma navegação para a mesma tela com outro período. null = o padrão da tela.
 */
export function usePeriodParam(horizon: [number, number]) {
  const [params, setParams] = useSearchParams();
  const linkYear = params.get(DRILL_PARAMS.year);
  const linkMonth = params.get(DRILL_PARAMS.month);
  const [first, last] = horizon;
  const fromLink = (y: string | null, m: string | null) => {
    const p = readPeriod(
      new URLSearchParams({ [DRILL_PARAMS.year]: y ?? '', [DRILL_PARAMS.month]: m ?? '' }),
    );
    return p && p.year >= first && p.year <= last ? p : null;
  };
  const [period, setState] = useState(() => fromLink(linkYear, linkMonth));
  useEffect(() => {
    const p = readPeriod(
      new URLSearchParams({ [DRILL_PARAMS.year]: linkYear ?? '', [DRILL_PARAMS.month]: linkMonth ?? '' }),
    );
    // Sem o período no endereço (ex.: o item do menu), a tela volta ao padrão dela.
    if (!linkYear && !linkMonth) return setState(null);
    if (!p || p.year < first || p.year > last) return;
    setState((prev) => (prev && prev.year === p.year && prev.month === p.month ? prev : p));
  }, [linkYear, linkMonth, first, last]);
  const setPeriod = (p: { year: number; month: number }) => {
    setState(p);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(DRILL_PARAMS.year, String(p.year));
        next.set(DRILL_PARAMS.month, String(p.month));
        return next;
      },
      { replace: true },
    );
  };
  return [period, setPeriod] as const;
}

/**
 * Para onde vai o número de uma perspectiva (T-144c): o dashboard dela ou, sem ele no perfil, os objetivos dela.
 * null = a pessoa não tem nenhuma das duas telas.
 */
export function perspectiveTarget(
  organizationId: string,
  path: DrillPath & { perspectiveId: string },
  sees: { dashboard: boolean; objectives: boolean },
) {
  if (sees.dashboard) return drillHref.perspective(organizationId, path);
  if (sees.objectives) {
    return `/empresas/${organizationId}/estrategia/objetivos?${query(path, ['projectId', 'perspectiveId', ...PERIOD])}`;
  }
  return null;
}

/** A perspectiva que mais puxa o número da empresa para baixo (a de menor performance no mês). */
export function worstPerspective<T extends { id: string; performance: number | null }>(items: T[]) {
  return items
    .filter((p) => p.performance !== null)
    .reduce<T | null>((worst, p) => (!worst || p.performance! < worst.performance! ? p : worst), null);
}
